/**
 * Admin chat attachments: the rules shared by the three chat screens (staff panel, customer view,
 * talent messages page) and the upload route. Same limits as the booking chat.
 */

export const ALLOWED_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf']
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024
export const ATTACHMENT_ACCEPT = '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf'

const PLACEHOLDER_PREFIX = '📎 '

export function formatBytes(n) {
  const b = Number(n) || 0
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`
  return `${(b / (1024 * 1024)).toFixed(1)} MB`
}

/** Browser-side check before uploading; mirrors the route's checks so the user hears it first. */
export function validateAttachment(file) {
  if (!file) return { ok: false, error: 'No file chosen' }
  if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) return { ok: false, error: 'Only JPEG, PNG and PDF files can be attached' }
  if (file.size > MAX_ATTACHMENT_BYTES) return { ok: false, error: `File is ${formatBytes(file.size)}; the limit is 10 MB` }
  return { ok: true }
}

/**
 * message_text is required on admin_messages. A file sent without words gets this text so the row,
 * the realtime event and the notification email all say what arrived. The screens hide it when the
 * attachment itself is shown.
 */
export function attachmentPlaceholder(fileName) {
  return `${PLACEHOLDER_PREFIX}${String(fileName || 'attachment').trim()}`
}

export function isAttachmentPlaceholder(text) {
  return typeof text === 'string' && text.startsWith(PLACEHOLDER_PREFIX)
}

/** Text to show for a message: hide the placeholder once the attachment is attached to the row. */
export function visibleMessageText(message) {
  if (!message) return ''
  if (message.attachment_url && isAttachmentPlaceholder(message.message_text)) return ''
  return message.message_text || ''
}

/** File name safe for a storage path: keep letters, digits, dot, dash, underscore. */
export function safeFileName(name) {
  const base = String(name || 'file').split(/[\\/]/).pop()
  const cleaned = base.replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '')
  return (cleaned || 'file').slice(0, 120)
}
